import { useId, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';

/* Selected Work Density & Motion Patch v1 — SelectedWorkWorkflow_Live (supplied component). */

type Mode = 'current' | 'rebuilt';
type NodeState = 'neutral' | 'manual' | 'active' | 'verified' | 'review';

type NodeDef = {
  id: string;
  label: string;
  sub: string;
  x: number;
  y: number;
  state: NodeState;
};

type EdgeDef = {
  id: string;
  d: string;
  tokenDelay: number;
};

const NODE_W = 112;
const NODE_H = 58;

const CURRENT_NODES: NodeDef[] = [
  { id: 'intake', label: 'MULTI-CHANNEL', sub: 'EMAIL · WEB · SUPPORT', x: 34, y: 70, state: 'neutral' },
  { id: 'triage', label: 'MANUAL TRIAGE', sub: 'CLASSIFY + PRIORITIZE', x: 176, y: 70, state: 'manual' },
  { id: 'entry', label: 'RE-ENTRY', sub: 'SPREADSHEET', x: 318, y: 70, state: 'manual' },
  { id: 'crm', label: 'CRM UPDATE', sub: 'DUPLICATED INPUT', x: 460, y: 70, state: 'manual' },
  { id: 'approval', label: 'APPROVAL', sub: 'WAITING', x: 318, y: 178, state: 'manual' },
  { id: 'response', label: 'CUSTOMER RESPONSE', sub: 'MANUAL FOLLOW-UP', x: 460, y: 178, state: 'neutral' },
];

const CURRENT_EDGES: EdgeDef[] = [
  { id: 'c1', d: 'M145 99 H177', tokenDelay: 0 },
  { id: 'c2', d: 'M287 99 H319', tokenDelay: 1.3 },
  { id: 'c3', d: 'M429 99 H461', tokenDelay: 2.6 },
  { id: 'c4', d: 'M516 127 V151 H374 V179', tokenDelay: 3.9 },
  { id: 'c5', d: 'M429 207 H461', tokenDelay: 5.2 },
];

const REBUILT_NODES: NodeDef[] = [
  { id: 'intake', label: 'UNIFIED INTAKE', sub: 'ONE ENTRY POINT', x: 34, y: 70, state: 'active' },
  { id: 'classify', label: 'AI CLASSIFY', sub: 'EXTRACT + PRIORITIZE', x: 176, y: 70, state: 'active' },
  { id: 'route', label: 'SYSTEM ROUTING', sub: 'ASSIGN + UPDATE', x: 318, y: 70, state: 'active' },
  { id: 'sync', label: 'CRM SYNC', sub: 'CONNECTED', x: 460, y: 70, state: 'verified' },
  { id: 'review', label: 'HUMAN REVIEW', sub: 'WHEN REQUIRED', x: 318, y: 178, state: 'review' },
  { id: 'response', label: 'CUSTOMER UPDATE', sub: 'READY', x: 460, y: 178, state: 'verified' },
];

const REBUILT_EDGES: EdgeDef[] = [
  { id: 'r1', d: 'M145 99 H177', tokenDelay: 0 },
  { id: 'r2', d: 'M287 99 H319', tokenDelay: 0.9 },
  { id: 'r3', d: 'M429 99 H461', tokenDelay: 1.8 },
  { id: 'r4', d: 'M374 127 V179', tokenDelay: 2.7 },
  { id: 'r5', d: 'M429 207 H461', tokenDelay: 3.6 },
  { id: 'r6', d: 'M516 127 V179', tokenDelay: 4.5 },
];

export default function SelectedWorkWorkflow({
  initialMode = 'rebuilt',
  className = '',
}: {
  initialMode?: Mode;
  className?: string;
}) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const reduceMotion = useReducedMotion();
  const id = useId().replace(/:/g, '');
  const nodes = mode === 'current' ? CURRENT_NODES : REBUILT_NODES;
  const edges = mode === 'current' ? CURRENT_EDGES : REBUILT_EDGES;

  return (
    <section className={`swwl ${className}`}>
      <div className="swwl-head">
        <div>
          <div className="swwl-kicker">WORKFLOW / COMPARE</div>
          <div className="swwl-title">Current state → rebuilt system</div>
        </div>
        <div className="swwl-switch" role="tablist" aria-label="Workflow view">
          {(['current', 'rebuilt'] as const).map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={mode === item}
              className={`swwl-tab ${mode === item ? 'is-active' : ''}`}
              onMouseEnter={() => {
                if (typeof window.matchMedia === 'function' && window.matchMedia('(hover:hover)').matches) setMode(item);
              }}
              onFocus={() => setMode(item)}
              onClick={() => setMode(item)}
            >
              {item.toUpperCase()}
              <i />
            </button>
          ))}
        </div>
      </div>

      <div className="swwl-stage">
        <svg viewBox="0 22 620 248" aria-label={mode === 'current' ? 'Current workflow' : 'Rebuilt workflow'}>
          <g className="swwl-guides">
            <path d="M24 38 H596" />
            <path d="M24 232 H596" />
          </g>

          <g key={`edges-${mode}`}>
            {edges.map((e, i) => (
              <motion.path
                key={e.id}
                id={`${id}-${e.id}`}
                d={e.d}
                className={`swwl-edge ${mode === 'rebuilt' ? 'is-rebuilt' : ''}`}
                initial={reduceMotion ? false : { opacity: 0 }}
                animate={{ opacity: mode === 'rebuilt' ? 0.88 : 0.5 }}
                transition={{
                  duration: reduceMotion ? 0 : 0.45,
                  delay: reduceMotion ? 0 : 0.05 + i * 0.045,
                  ease: [0.22, 1, 0.36, 1],
                }}
              />
            ))}

            {!reduceMotion && edges.map((e) => (
              <circle
                key={`token-${e.id}`}
                r={mode === 'rebuilt' ? 2.4 : 1.9}
                className={`swwl-token ${mode}`}
                opacity={0}
              >
                <animateMotion
                  dur={mode === 'rebuilt' ? '4.6s' : '6.4s'}
                  begin={`${e.tokenDelay}s`}
                  repeatCount="indefinite"
                  path={e.d}
                />
                <animate
                  attributeName="opacity"
                  dur={mode === 'rebuilt' ? '4.6s' : '6.4s'}
                  begin={`${e.tokenDelay}s`}
                  repeatCount="indefinite"
                  values="0;1;1;0;0"
                  keyTimes="0;0.15;0.8;0.95;1"
                />
              </circle>
            ))}
          </g>

          <g key={`nodes-${mode}`}>
            {nodes.map((n, i) => (
              <g key={n.id} transform={`translate(${n.x} ${n.y})`}>
                <motion.g
                  className={`swwl-node is-${n.state}`}
                  initial={reduceMotion ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: reduceMotion ? 0 : 0.28,
                    delay: reduceMotion ? 0 : 0.05 + i * 0.035,
                  }}
                >
                  <rect x="0" y="0" width={NODE_W} height={NODE_H} />
                  <path d={`M0 18 H${NODE_W}`} className="swwl-sep" />
                  <circle cx="9" cy="9" r="2.2" className="swwl-dot" />
                  <text x="10" y="36" className="swwl-label">{n.label}</text>
                  <text x="10" y="49" className="swwl-sub">{n.sub}</text>
                </motion.g>
              </g>
            ))}
          </g>

          <g className="swwl-meta">
            <text x="24" y="258">
              {mode === 'current' ? 'STATE / MANUAL COORDINATION' : 'STATE / ORCHESTRATED WORKFLOW'}
            </text>
            <text x="596" y="258" textAnchor="end">
              {mode === 'current' ? 'FRICTION / VISIBLE' : 'HUMAN REVIEW / PRESERVED'}
            </text>
          </g>
        </svg>
      </div>

      {/* Mobile-only vertical flow: same nodes, same mode state */}
      <ol key={`m-${mode}`} className={`swwl-mobile is-${mode}`} aria-label={mode === 'current' ? 'Current workflow' : 'Rebuilt workflow'}>
        {nodes.map((n, i) => (
          <motion.li
            key={n.id}
            className={`swwl-mnode is-${n.state} ${n.y > 100 ? 'is-branch' : ''}`}
            initial={reduceMotion ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.28, delay: reduceMotion ? 0 : i * 0.04 }}
          >
            <span className="swwl-mdot" aria-hidden="true" />
            <span className="swwl-mtext">
              <span className="swwl-mlabel">{n.label}</span>
              <span className="swwl-msub">{n.sub}</span>
            </span>
            <span className="swwl-mstep">{String(i + 1).padStart(2, '0')}</span>
          </motion.li>
        ))}
        <li className="swwl-mmeta">
          {mode === 'current' ? 'FRICTION / VISIBLE' : 'HUMAN REVIEW / PRESERVED'}
        </li>
      </ol>

      <style>{styles}</style>
    </section>
  );
}

const styles = `
.swwl{
  --paper:#ffffff; --ink:#222a4c; --muted:#687386;
  --steel:#c8cbd3; --steel2:#e7ebf0;
  --blue:#0f62fe; --blueSoft:#78a9ff; --green:#168b61;
  font-family:"Geist","IBM Plex Sans",system-ui,sans-serif;
  color:var(--ink); background:var(--paper);
  display:flex;flex-direction:column;
}
.swwl-head{
  min-height:68px;padding:12px 18px 10px;
  display:flex;align-items:flex-end;justify-content:space-between;gap:20px;
  border-bottom:1px solid var(--steel2);
}
.swwl-kicker{
  margin-bottom:5px;color:var(--muted);
  font:500 8.5px/1 "Geist Mono","IBM Plex Mono",monospace;letter-spacing:.13em
}
.swwl-title{font-size:18px;line-height:1.05;letter-spacing:-.025em}
.swwl-switch{display:flex;gap:18px}
.swwl-tab{
  position:relative;border:0;background:transparent;padding:7px 0 5px;color:var(--muted);
  cursor:pointer;white-space:nowrap;font:500 8.5px/1 "Geist Mono","IBM Plex Mono",monospace;letter-spacing:.12em
}
.swwl-tab i{
  position:absolute;left:0;right:0;bottom:0;height:1px;background:var(--blue);
  transform:scaleX(0);transform-origin:left;transition:transform .24s cubic-bezier(.22,1,.36,1)
}
.swwl-tab.is-active{color:var(--blue)}
.swwl-tab.is-active i{transform:scaleX(1)}
.swwl-stage{
  overflow:hidden;padding:2px 12px 0;flex:1 1 auto;min-height:0;
  display:flex;align-items:center;justify-content:center;
  background-image:
    linear-gradient(to right, rgba(200,203,211,.18) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(200,203,211,.18) 1px, transparent 1px);
  background-size:22px 22px;background-position:center center;
}
.swwl-stage svg{display:block;width:100%;height:auto;overflow:hidden}
.swwl-guides path{
  fill:none;stroke:var(--steel);stroke-width:.7;stroke-dasharray:2 5;opacity:.42;vector-effect:non-scaling-stroke
}
.swwl-edge{
  fill:none;stroke:var(--steel);stroke-width:1;stroke-linecap:round;stroke-linejoin:round;
  vector-effect:non-scaling-stroke
}
.swwl-edge.is-rebuilt{stroke:var(--blueSoft)}
.swwl-token{pointer-events:none}
.swwl-token.current{fill:#9ca6b7;fill-opacity:.55}
.swwl-token.rebuilt{fill:var(--blue);fill-opacity:.9}
.swwl-node rect{
  fill:rgba(251,253,255,.97);stroke:var(--steel);stroke-width:1;vector-effect:non-scaling-stroke
}
.swwl-sep{fill:none;stroke:var(--steel2);stroke-width:1;vector-effect:non-scaling-stroke}
.swwl-dot{fill:var(--steel)}
.swwl-label{
  fill:var(--ink);
  font:500 8.8px/1 "Geist Mono","IBM Plex Mono",monospace;letter-spacing:.055em
}
.swwl-sub{
  fill:var(--muted);
  font:500 6.7px/1 "Geist Mono","IBM Plex Mono",monospace;letter-spacing:.07em
}
.swwl-node.is-active rect{stroke:var(--blueSoft)}
.swwl-node.is-active .swwl-dot{
  fill:var(--blue);
  animation:swwlPulse 2.4s ease-in-out infinite
}
.swwl-node.is-verified rect{stroke:rgba(22,139,97,.55)}
.swwl-node.is-verified .swwl-dot{fill:var(--green)}
.swwl-node.is-manual .swwl-dot{fill:#8b95a5}
.swwl-node.is-review .swwl-dot{fill:var(--paper);stroke:var(--ink);stroke-width:1}
.swwl-meta text{
  fill:var(--muted);
  font:500 6.8px/1 "Geist Mono","IBM Plex Mono",monospace;letter-spacing:.09em
}
@keyframes swwlPulse{
  0%,100%{opacity:.45}
  50%{opacity:1}
}
.swwl-mobile{display:none}
@media(max-width:760px){
  .swwl-head{align-items:flex-start;flex-direction:column;gap:12px;padding:14px 16px 0;border-bottom:0}
  .swwl-kicker{font-size:10px}
  .swwl-switch{gap:22px}
  .swwl-tab{font-size:11px;padding:12px 0 10px;min-height:40px}
  .swwl-stage{display:none}
  .swwl-mobile{display:flex;flex-direction:column;list-style:none;margin:0;padding:16px;position:relative;border-top:1px solid var(--steel2);
    background-image:linear-gradient(to right, rgba(200,203,211,.18) 1px, transparent 1px),linear-gradient(to bottom, rgba(200,203,211,.18) 1px, transparent 1px);background-size:22px 22px}
  .swwl-mnode{position:relative;display:grid;grid-template-columns:14px minmax(0,1fr) auto;align-items:center;gap:12px;padding:10px 12px;margin-bottom:10px;background:rgba(251,253,255,.97);border:1px solid var(--steel)}
  .swwl-mnode:nth-last-child(n+3):after{content:"";position:absolute;left:18px;top:100%;height:10px;width:1px;background:var(--steel)}
  .is-rebuilt .swwl-mnode:nth-last-child(n+3):after{background:var(--blueSoft)}
  .swwl-mnode.is-branch{margin-left:20px}
  .swwl-mnode.is-branch:before{content:"BRANCH";position:absolute;right:12px;top:-1px;transform:translateY(-50%);padding:0 4px;background:var(--paper);color:var(--muted);font:500 8px/1.4 "Geist Mono",monospace;letter-spacing:.1em}
  .swwl-mdot{width:7px;height:7px;border-radius:50%;background:var(--steel);justify-self:center}
  .swwl-mtext{display:flex;flex-direction:column;gap:3px;min-width:0}
  .swwl-mlabel{font:500 12px/1.2 "Geist Mono","IBM Plex Mono",monospace;letter-spacing:.05em;color:var(--ink)}
  .swwl-msub{font:500 10px/1.3 "Geist Mono","IBM Plex Mono",monospace;letter-spacing:.06em;color:var(--muted)}
  .swwl-mstep{font:500 10px/1 "Geist Mono",monospace;color:var(--muted)}
  .swwl-mnode.is-active{border-color:var(--blueSoft)}
  .swwl-mnode.is-active .swwl-mdot{background:var(--blue);animation:swwlPulse 2.4s ease-in-out infinite}
  .swwl-mnode.is-verified{border-color:rgba(22,139,97,.55)}
  .swwl-mnode.is-verified .swwl-mdot{background:var(--green)}
  .swwl-mnode.is-manual .swwl-mdot{background:#8b95a5}
  .swwl-mnode.is-review .swwl-mdot{background:var(--paper);border:1px solid var(--ink)}
  .swwl-mmeta{padding-top:2px;color:var(--muted);font:500 10px/1.4 "Geist Mono",monospace;letter-spacing:.09em}
}
@media(prefers-reduced-motion:reduce){
  .swwl-node.is-active .swwl-dot,.swwl-mnode.is-active .swwl-mdot{animation:none}
}
`;